import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, Check, ImageIcon, Plus, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { bySection, sectionName, sectionOf } from '../../shared/meals'
import { PictureImage } from '@/components/cookbook/Pages'
import { ConfirmButton } from '@/components/ConfirmButton'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { albumPhotoUrl, streamGroups, useStream } from '@/lib/albums'
import { api } from '@/lib/api'
import {
  useCookbook,
  useFamilyRecipes,
  type Cookbook,
  type CookbookPage,
  type PickableRecipe,
  type PictureChoice,
} from '@/lib/cookbooks'
import { usePageFrame } from '@/lib/frame'
import { photoUrl } from '@/lib/photos'
import { NO_BOOK } from '@/lib/recipes'
import { cn } from '@/lib/utils'
import { ShareWith } from '@/components/ShareWith'
import { audienceBody, audienceFrom, type Audience } from '@/lib/spaces'
import { PageLoader } from '@/components/Loader'

export function CookbookEdit() {
  const { id = '' } = useParams()
  const cookbook = useCookbook(id)
  if (cookbook.isPending) return <PageLoader />
  if (cookbook.isError) return <FormError error={cookbook.error} />
  if (!cookbook.data.mine)
    return <FormError error={new Error('Only the person who made this cookbook can change it.')} />
  return <Editor cookbook={cookbook.data} />
}

// Making a cookbook: which recipes go in and in what order, the picture beside each, and its cover details.
function Editor({ cookbook }: { cookbook: Cookbook }) {
  usePageFrame(cookbook.familyId, {
    title: 'Edit cookbook',
    back: { to: `/cookbook/${cookbook.id}`, label: 'Back to the cookbook' },
  })
  const queryClient = useQueryClient()
  const recipes = useFamilyRecipes(cookbook.familyId)
  const [order, setOrder] = useState(() => cookbook.pages.map((x) => x.recipe.id))
  const [picking, setPicking] = useState<CookbookPage | null>(null)
  const save = useMutation({
    mutationFn: (recipeIds: string[]) =>
      api(`/cookbooks/${cookbook.id}/recipes`, { method: 'PUT', body: { recipeIds } }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['cookbook', cookbook.id] }),
  })
  const change = (next: string[]) => {
    setOrder(next)
    save.mutate(next)
  }
  const byId = useMemo(() => new Map((recipes.data?.recipes ?? []).map((x) => [x.id, x])), [recipes.data])
  const pages = new Map(cookbook.pages.map((x) => [x.recipe.id, x]))
  // Divided by meal, the list shows the book's sections, and a recipe moves up and down within its own.
  const [sections, setSections] = useState(cookbook.sections)
  const mealsOf = (id: string) => pages.get(id)?.recipe.meals ?? byId.get(id)?.meals ?? []
  const anyMeals = order.some((id) => mealsOf(id).length > 0)
  const divided = sections && anyMeals
  const shown = divided ? bySection(order, mealsOf) : order
  const sectionAt = (i: number) => sectionOf(mealsOf(shown[i]!))
  const canMove = (i: number, by: number) =>
    i + by >= 0 && i + by < shown.length && (!divided || sectionAt(i + by) === sectionAt(i))
  // Turned off, the list goes back to the maker's own order, which only the server still knows.
  const divide = useMutation({
    mutationFn: async (on: boolean) => {
      await api(`/cookbooks/${cookbook.id}`, { method: 'PATCH', body: { sections: on } })
      return api<Cookbook>(`/cookbooks/${cookbook.id}`)
    },
    onMutate: (on) => setSections(on),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['cookbook', cookbook.id], fresh)
      setOrder(fresh.pages.map((x) => x.recipe.id))
    },
    onError: (_e, on) => setSections(!on),
  })

  return (
    <div className="flex flex-col gap-10 pb-16">
      <header className="flex flex-col gap-2">
        <Link to={`/cookbook/${cookbook.id}`} className="self-start eyebrow hover:underline">
          {cookbook.title}
        </Link>
        <h1 className="text-display">Edit cookbook</h1>
      </header>

      <section aria-labelledby="in-book" className="flex flex-col gap-4">
        <div className="flex items-baseline justify-between gap-4">
          <h2 id="in-book" className="text-title">
            Recipes in it
          </h2>
          <span className="text-sub text-muted-foreground" aria-live="polite">
            {save.isPending ? 'Saving…' : `${order.length} ${order.length === 1 ? 'recipe' : 'recipes'}`}
          </span>
        </div>
        <FormError error={save.error ?? divide.error} />
        {anyMeals && (
          <label className="flex min-h-11 cursor-pointer items-center gap-3 self-start text-sm">
            <input
              type="checkbox"
              className="size-5 accent-primary"
              checked={sections}
              onChange={(e) => divide.mutate(e.target.checked)}
            />
            Divide it by meal, each section opening on its own page
          </label>
        )}
        {order.length === 0 ? (
          <p className="rounded-2xl border border-dashed p-5 text-muted-foreground">
            Add recipes from the list below. Each one gets a spread: its picture on the left, the recipe on the right.
          </p>
        ) : (
          <ol className="flex flex-col divide-y divide-border rounded-2xl border">
            {shown.map((id, i) => {
              const page = pages.get(id)
              const recipe = byId.get(id)
              const title = page?.recipe.title ?? recipe?.title ?? ''
              const opens = divided && (i === 0 || sectionAt(i - 1) !== sectionAt(i))
              return (
                <li key={id} className="flex flex-wrap items-center gap-3 p-2.5 pl-3">
                  {opens && (
                    <p className="w-full pt-2 text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
                      {sectionName(sectionAt(i))}
                    </p>
                  )}
                  <span className="w-5 shrink-0 text-right text-sub text-muted-foreground">{i + 1}</span>
                  <button
                    type="button"
                    disabled={!page}
                    onClick={() => page && setPicking(page)}
                    aria-label={`Choose the picture for ${title}`}
                    className="relative size-12 shrink-0 overflow-hidden rounded-lg bg-secondary outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_img]:size-full [&_img]:object-cover"
                  >
                    {page?.picture ? (
                      <PictureImage picture={page.picture} alt="" sizes="48px" />
                    ) : (
                      <ImageIcon className="m-auto size-5 text-muted-foreground" aria-hidden />
                    )}
                  </button>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{title}</span>
                    <button
                      type="button"
                      disabled={!page}
                      onClick={() => page && setPicking(page)}
                      className="text-sub text-muted-foreground underline-offset-4 hover:underline"
                    >
                      Change picture
                    </button>
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${title} up`}
                    disabled={!canMove(i, -1)}
                    onClick={() => change(move(shown, i, -1))}
                  >
                    <ArrowUp aria-hidden />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Move ${title} down`}
                    disabled={!canMove(i, 1)}
                    onClick={() => change(move(shown, i, 1))}
                  >
                    <ArrowDown aria-hidden />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Take ${title} out`}
                    onClick={() => change(order.filter((x) => x !== id))}
                  >
                    <X aria-hidden />
                  </Button>
                </li>
              )
            })}
          </ol>
        )}
        {order.length > 0 && (
          <Button asChild size="lg" className="self-start">
            <Link to={`/cookbook/${cookbook.id}`}>Open the cookbook</Link>
          </Button>
        )}
      </section>

      <AddRecipes
        recipes={recipes.data?.recipes}
        error={recipes.error}
        chosen={order}
        onToggle={(id) => change(order.includes(id) ? order.filter((x) => x !== id) : [...order, id])}
      />

      <Details cookbook={cookbook} />

      <Dialog open={!!picking} onOpenChange={(open) => !open && setPicking(null)}>
        <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-2xl">
          {picking && <PicturePicker cookbook={cookbook} page={picking} onDone={() => setPicking(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  )
}

const move = (list: string[], i: number, by: number) => {
  const next = [...list]
  const [item] = next.splice(i, 1)
  next.splice(i + by, 0, item!)
  return next
}

// Every recipe you can see in the circle, by book, to add or take out with a tap.
function AddRecipes({
  recipes,
  error,
  chosen,
  onToggle,
}: {
  recipes?: PickableRecipe[]
  error: Error | null
  chosen: string[]
  onToggle: (id: string) => void
}) {
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const shown = (recipes ?? []).filter(
    (x) => !q || x.title.toLowerCase().includes(q) || (x.book?.title ?? NO_BOOK).toLowerCase().includes(q),
  )
  // Grouped by book, with the recipes in no book together under their own heading.
  const bookOf = (x: PickableRecipe) => x.book ?? { id: '', title: NO_BOOK }
  const books = [...new Map(shown.map((x) => [bookOf(x).id, bookOf(x)])).values()]
  return (
    <section aria-labelledby="add-recipes" className="flex flex-col gap-4">
      <h2 id="add-recipes" className="text-title">
        Add recipes
      </h2>
      <FormError error={error} />
      <div className="relative">
        <Search className="absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          aria-label="Find a recipe"
          placeholder="Find a recipe"
          className="pl-10"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {recipes && shown.length === 0 && (
        <p className="text-muted-foreground">{q ? 'No recipes match.' : 'There are no recipes to choose from yet.'}</p>
      )}
      {books.map((book) => (
        <div key={book.id} className="flex flex-col gap-1">
          <h3 className="eyebrow text-muted-foreground">{book.title}</h3>
          <ul className="flex flex-col">
            {shown
              .filter((x) => bookOf(x).id === book.id)
              .map((recipe) => {
                const on = chosen.includes(recipe.id)
                return (
                  <li key={recipe.id}>
                    <button
                      type="button"
                      aria-pressed={on}
                      onClick={() => onToggle(recipe.id)}
                      className="flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    >
                      <span className="size-10 shrink-0 overflow-hidden rounded-lg bg-secondary">
                        {recipe.cover && (
                          <img
                            src={photoUrl(recipe.cover, 'thumb')}
                            alt=""
                            loading="lazy"
                            className="size-full object-cover"
                          />
                        )}
                      </span>
                      <span className="min-w-0 flex-1 truncate">{recipe.title}</span>
                      <span
                        className={cn(
                          'grid size-8 shrink-0 place-items-center rounded-full border',
                          on && 'border-brand bg-brand text-brand-foreground',
                        )}
                      >
                        {on ? <Check className="size-4" aria-hidden /> : <Plus className="size-4" aria-hidden />}
                      </span>
                      <span className="sr-only">{on ? 'In the cookbook' : 'Add'}</span>
                    </button>
                  </li>
                )
              })}
          </ul>
        </div>
      ))}
    </section>
  )
}

// The picture beside a recipe: its own card photos, or any photo from the circle's albums.
function PicturePicker({ cookbook, page, onDone }: { cookbook: Cookbook; page: CookbookPage; onDone: () => void }) {
  const queryClient = useQueryClient()
  const stream = useStream(cookbook.familyId, 'year', false)
  const photos = streamGroups(stream.data?.pages ?? []).flatMap((g) => g.photos)
  const chosen = page.chosen ?? null
  const pick = useMutation({
    mutationFn: (picture: PictureChoice | null) =>
      api(`/cookbooks/${cookbook.id}/recipes/${page.recipe.id}`, { method: 'PATCH', body: { picture } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cookbook', cookbook.id] })
      onDone()
    },
  })
  const is = (kind: string, id: string) => chosen?.kind === kind && chosen.id === id
  const tile =
    'relative aspect-square overflow-hidden rounded-xl bg-secondary outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-60'
  const tick = (
    <span className="absolute top-1.5 right-1.5 grid size-6 place-items-center rounded-full bg-primary text-primary-foreground">
      <Check className="size-3.5" aria-hidden />
    </span>
  )
  return (
    <div className="flex flex-col gap-6">
      <DialogHeader>
        <DialogTitle>Picture for {page.recipe.title}</DialogTitle>
        <DialogDescription>It fills the page facing the recipe. A photo of the dish works best.</DialogDescription>
      </DialogHeader>
      <FormError error={pick.error} />
      {page.recipe.photos.length > 0 && (
        <section className="flex flex-col gap-2" aria-label="The recipe’s own photos">
          <h3 className="text-sm font-semibold">The recipe’s photos</h3>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {page.recipe.photos.map((photo, i) => {
              const on = chosen ? is('recipe', photo.id) : i === 0
              return (
                <button
                  key={photo.id}
                  type="button"
                  className={tile}
                  aria-pressed={on}
                  aria-label={`Photo ${i + 1} of the recipe`}
                  disabled={pick.isPending}
                  onClick={() => pick.mutate(i === 0 ? null : { kind: 'recipe', id: photo.id })}
                >
                  <img src={photoUrl(photo.ref, 'thumb')} alt="" className="size-full object-cover" />
                  {on && tick}
                </button>
              )
            })}
          </div>
        </section>
      )}
      <section className="flex flex-col gap-2" aria-label="Photos from the circle’s albums">
        <h3 className="text-sm font-semibold">From your photos</h3>
        <FormError error={stream.error} />
        {stream.data && photos.length === 0 && (
          <p className="text-sub text-muted-foreground">Photos added to albums show here.</p>
        )}
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map((photo) => {
            const on = is('album', photo.id)
            return (
              <button
                key={photo.id}
                type="button"
                className={tile}
                aria-pressed={on}
                aria-label={photo.caption || 'A photo from the albums'}
                disabled={pick.isPending}
                onClick={() => pick.mutate({ kind: 'album', id: photo.id })}
              >
                <img src={albumPhotoUrl(photo.ref, 'thumb')} alt="" loading="lazy" className="size-full object-cover" />
                {on && tick}
              </button>
            )
          })}
        </div>
        {stream.hasNextPage && (
          <Button
            variant="outline"
            className="self-center"
            disabled={stream.isFetchingNextPage}
            onClick={() => void stream.fetchNextPage()}
          >
            Show more photos
          </Button>
        )}
      </section>
      {chosen && (
        <Button variant="ghost" className="self-start" disabled={pick.isPending} onClick={() => pick.mutate(null)}>
          {page.recipe.photos.length ? 'Use the recipe’s own photo' : 'Leave the page without a photo'}
        </Button>
      )}
    </div>
  )
}

// The cover details: its name, the line under it, the dedication, who it's for, and taking it down.
function Details({ cookbook }: { cookbook: Cookbook }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [title, setTitle] = useState(cookbook.title)
  const [subtitle, setSubtitle] = useState(cookbook.subtitle)
  const [dedication, setDedication] = useState(cookbook.dedication)
  const [who, setWho] = useState<Audience>(() =>
    cookbook.sharedWith
      ? audienceFrom(cookbook)
      : { everyone: false, spaceIds: cookbook.space ? [cookbook.space.id] : [] },
  )
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: () =>
      api(`/cookbooks/${cookbook.id}`, {
        method: 'PATCH',
        body: {
          title: title.trim(),
          subtitle: subtitle.trim(),
          dedication: dedication.trim(),
          ...audienceBody(who),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cookbook', cookbook.id] })
      await queryClient.invalidateQueries({ queryKey: ['cookbooks', cookbook.familyId] })
      setSaved(true)
    },
  })
  const remove = useMutation({
    mutationFn: () => api(`/cookbooks/${cookbook.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cookbooks', cookbook.familyId] })
      navigate(`/circle/${cookbook.familyId}/recipes`, { replace: true })
    },
  })
  return (
    <section aria-labelledby="cover-details" className="flex flex-col gap-5 border-t pt-8">
      <h2 id="cover-details" className="text-title">
        Cover
      </h2>
      <form
        className="grid gap-5"
        onSubmit={(e) => {
          e.preventDefault()
          setSaved(false)
          save.mutate()
        }}
      >
        <div className="grid gap-2">
          <Label htmlFor="cookbook-rename">Cookbook name</Label>
          <Input
            id="cookbook-rename"
            required
            maxLength={80}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="cookbook-line">A line under it</Label>
          <Input id="cookbook-line" maxLength={120} value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="cookbook-dedication">Dedication</Label>
          <Textarea
            id="cookbook-dedication"
            rows={3}
            maxLength={600}
            aria-describedby="cookbook-dedication-hint"
            value={dedication}
            onChange={(e) => setDedication(e.target.value)}
          />
          <p id="cookbook-dedication-hint" className="text-sub text-muted-foreground">
            Optional. It gets a page of its own after the cover.
          </p>
        </div>
        <div className="grid gap-2">
          <p className="text-sm font-medium">Who’s this for?</p>
          <ShareWith here={cookbook.familyId} value={who} onChange={setWho} onlyMeHint="Just you, until you share it" />
          <p className="text-sub text-muted-foreground">
            Each person sees only the recipes they can already see, wherever the cookbook is shared.
          </p>
        </div>
        <FormError error={save.error} />
        <div className="flex items-center gap-3">
          <Button type="submit" size="lg" disabled={save.isPending || !title.trim()}>
            Save cover
          </Button>
          {saved && <span className="text-sub text-muted-foreground">Saved.</span>}
        </div>
      </form>
      <div className="flex flex-col gap-2 border-t pt-5">
        <FormError error={remove.error} />
        <ConfirmButton
          label="Delete this cookbook"
          title={`Delete ${cookbook.title}?`}
          description="The cookbook is removed for everyone in the circle. Its recipes stay in their books."
          confirm="Delete cookbook"
          disabled={remove.isPending}
          onConfirm={() => remove.mutate()}
        />
      </div>
    </section>
  )
}
